import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  MapPin, Calendar, ArrowRight } from 'lucide-react';

const UTAustin: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UT Austin";

    return (
        <div className="min-h-screen bg-white font-sans text-[#333f48] selection:bg-[#bf5700] selection:text-white">
            {/* Utility Bar */}
            <div className="bg-[#bf5700] text-white py-1 px-6 flex justify-end gap-6 text-[11px] font-bold uppercase tracking-wider">
                <a href="#" className="hover:underline">Canvas</a>
                <a href="#" className="hover:underline">Directory</a>
                <a href="#" className="hover:underline">Offices</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-200">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="text-3xl font-serif font-bold text-[#bf5700] tracking-tight leading-none">{schoolName.split(' ')[0].toUpperCase()}</span>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#333f48]">{schoolName}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#333f48] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#bf5700] transition-colors relative group">
                            About
                            <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#bf5700] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#bf5700] transition-colors relative group">
                            Academics
                            <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#bf5700] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#bf5700] transition-colors relative group">
                            Admissions
                            <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#bf5700] transition-all group-hover:w-full"></span>
                        </a>
                        <a href="#" className="hover:text-[#bf5700] transition-colors relative group">
                            Research
                            <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#bf5700] transition-all group-hover:w-full"></span>
                        </a>
                         <a href="#" className="hover:text-[#bf5700] transition-colors relative group">
                            Athletics
                            <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-[#bf5700] transition-all group-hover:w-full"></span>
                        </a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer hover:text-[#bf5700]" size={22} />
                         <Menu className="cursor-pointer lg:hidden hover:text-[#bf5700]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - The Tower */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1531214062080-660c679a941e?q=80&w=2070&auto=format&fit=crop" 
                    alt="UT Tower" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#333f48]/90 via-transparent to-transparent"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-20">
                     <div className="max-w-4xl mx-auto text-center text-white">
                         <h1 className="text-5xl md:text-8xl font-serif font-bold mb-6 leading-none">
                             What starts here changes the world.
                         </h1>
                         <Button className="bg-[#bf5700] hover:bg-[#a64b00] text-white font-bold uppercase tracking-widest px-12 h-16 rounded-sm text-lg shadow-lg">
                             Visit {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                </div>
            </header>

            {/* News - Burnt Orange */}
            <section className="bg-white py-24 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto">
                    <div className="flex justify-between items-end mb-12 border-b-2 border-[#bf5700] pb-4">
                        <h2 className="text-4xl font-serif font-bold text-[#333f48]">News from the Forty Acres</h2>
                        <a href="#" className="text-[#bf5700] font-bold uppercase tracking-widest text-xs hover:underline mb-2">More News</a>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {[
                            { title: `${schoolName.split(' ')[0]} researchers develop new battery technology for EVs`, img: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?q=80&w=2072&auto=format&fit=crop" },
                            { title: "Moody College of Communication launches immersive media lab", img: "https://images.unsplash.com/photo-1535451801241-b5395e1d4a1b?q=80&w=1935&auto=format&fit=crop" },
                            { title: "Longhorn Band celebrates 100 years of tradition", img: "https://images.unsplash.com/photo-1514525253440-b39345208668?q=80&w=2070&auto=format&fit=crop" }
                        ].map((item, i) => (
                            <div key={i} className="group cursor-pointer">
                                <div className="h-60 mb-6 overflow-hidden relative">
                                    <div className="absolute top-0 left-0 w-full h-1 bg-[#bf5700] z-10 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                                    <img src={item.img} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt={item.title}/>
                                </div>
                                <h3 className="text-2xl font-bold text-[#333f48] leading-tight group-hover:text-[#bf5700] transition-colors">
                                    {item.title}
                                </h3>
                                <div className="mt-4 flex items-center gap-2 text-[#bf5700] font-bold text-sm uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                    Read Story <ArrowRight size={16}/>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
             <footer className="bg-[#333f48] text-white py-16 px-6 md:px-12 border-t-[6px] border-[#bf5700]">
                 <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                     <div className="max-w-xs">
                         <span className="font-serif font-bold text-3xl text-white mb-2 block">{schoolName.split(' ')[0].toUpperCase()}</span>
                         <p className="text-gray-400 mb-6">
                             {schoolName} <br/>
                             Austin, Texas 78712 <br/>
                             512-471-3434
                         </p>
                     </div>

                     <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-4 font-bold text-[#bf5700] uppercase text-xs tracking-widest">
                         <a href="#" className="hover:text-white">Emergency Information</a>
                         <a href="#" className="hover:text-white">Site Policies</a>
                         <a href="#" className="hover:text-white">Web Accessibility</a>
                         <a href="#" className="hover:text-white">Web Privacy Policy</a>
                         <a href="#" className="hover:text-white">Adobe Reader</a>
                     </div>
                 </div>
                 <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-500">
                     © 2026 {schoolName}
                 </div>
             </footer>
        </div>
    );
};

export default UTAustin;
